/**
 * analysisSummary.js
 *
 * Single source for the AI Explorer post-analysis stat line.
 *
 * Two surfaces render this sentence — the "Analysis Complete" box in
 * AIExplorerPage.js and the PDF header in pdfExport.js. They previously built
 * it independently and disagreed with each other on the same run: the green box
 * said "determined only 56 of them were relevant" while the PDF said
 * "determined all of them were relevant", from the same metadata. Both were also
 * wrong about what the numbers meant. Keeping the wording in one function is
 * what makes a repeat of that impossible; do not inline this text anywhere.
 *
 * What the numbers actually are:
 * - BUNDLE (searchMetadata.entriesSentForAnalysis) — unique entries serialized
 *   into the prompt sent to the provider. This is the real "how much was looked
 *   at" figure. NOT searchMetadata.totalAnalyzed, which is the retrieval cap and
 *   is filled on most runs with no relevance judgement behind it.
 * - CITED_UNIQUE (searchMetadata.totalCited) — unique entries cited in the
 *   response.
 * - INSTANCES (searchMetadata.totalCitationInstances) — total citation
 *   occurrences, which can exceed CITED_UNIQUE when one entry is cited twice.
 *
 * The model name is deliberately absent — "The AI" stands in for it. Model
 * attribution lives on the stored synthesis (aiProvider / aiModel) and is not
 * lost by omitting it here.
 */

/**
 * Build the post-analysis stat sentence from response metadata.
 *
 * Degrades honestly: analyses run before entriesSentForAnalysis existed have no
 * bundle count, and there is no way to derive one after the fact. Those render
 * the citation sentence alone rather than substituting a different number into
 * the bundle slot — a wrong number here is worse than a missing one, which is
 * the failure this whole change exists to correct.
 *
 * @param {Object} searchMetadata - response.searchMetadata
 * @returns {string} The sentence, or '' when there is nothing truthful to say
 */
export function buildAnalysisSummary(searchMetadata) {
  if (!searchMetadata) return '';

  const {
    entriesSentForAnalysis,
    totalCited,
    totalCitationInstances
  } = searchMetadata;

  const cited = Number.isFinite(totalCited) ? totalCited : 0;
  const instances = Number.isFinite(totalCitationInstances) ? totalCitationInstances : 0;

  // A count of exactly 1 reads as the word "one" with a singular noun; anything
  // else stays a numeral with a plural noun. "identified 1 entries" and
  // "cited 1 of them, 1 time" both read as bugs, and the sentence is the one
  // place a user is told what the system actually did.
  const countWord = (n) => (n === 1 ? 'one' : String(n));
  const timesWord = instances === 1 ? 'time' : 'times';

  // Strict numeric check. A pre-change synthesis has the field absent
  // (undefined); null and NaN are treated the same way. Note that 0 is a
  // legitimate value and must NOT fall through to the degraded form.
  const hasBundle = Number.isFinite(entriesSentForAnalysis);

  if (!hasBundle) {
    const entryWord = cited === 1 ? 'entry' : 'entries';
    return `The AI cited ${countWord(cited)} ${entryWord}, ${countWord(instances)} ${timesWord}.`;
  }

  const bundleEntryWord = entriesSentForAnalysis === 1 ? 'entry' : 'entries';

  return (
    `CKG searched your knowledge base and identified ${countWord(entriesSentForAnalysis)} ` +
    `${bundleEntryWord} relevant to your question for analysis. ` +
    `The AI cited ${countWord(cited)} of them, ${countWord(instances)} ${timesWord}.`
  );
}

export default buildAnalysisSummary;
